import { datadogLogs } from '@datadog/browser-logs';
import { useQuery } from '@tanstack/react-query';
import { SHORT_API_REQUEST_TIMEOUT_MS } from '@web-app/constants/timeouts';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

export interface VerifyRoomParams {
  roomName: string;
}

type VerifyRoomBaseResponse = {
  success: true;
};

export type VerifyRoomRedirectResponse = VerifyRoomBaseResponse & {
  redirect: true;
  newRoomName: string;
};

export type VerifyRoomResponse =
  | VerifyRoomBaseResponse
  | VerifyRoomRedirectResponse;

export const isRedirectResponse = (
  data: VerifyRoomResponse,
): data is VerifyRoomRedirectResponse => 'redirect' in data && data.redirect;

export interface VerifyRoomEndedError extends Error {
  reason: 'ended';
  status: 'Occurred';
}

export interface VerifyRoomUnavailableError extends Error {
  reason: 'unavailable';
  status: string;
}

export interface VerifyRoomNotFoundError extends Error {
  reason: 'not_found';
}

export interface VerifyRoomError extends Error {
  reason: 'error';
}

export type VerifyRoomMutationError =
  | VerifyRoomEndedError
  | VerifyRoomUnavailableError
  | VerifyRoomNotFoundError
  | VerifyRoomError;

/**
 * Helper to create typed errors with reason
 */
const createError = (
  message: string,
  reason: 'ended' | 'unavailable' | 'not_found' | 'error',
  status?: string,
): VerifyRoomMutationError => {
  const error = new Error(message) as VerifyRoomMutationError;
  error.reason = reason;
  if (reason === 'ended') {
    (error as VerifyRoomEndedError).status = 'Occurred';
  } else if (reason === 'unavailable' && status) {
    (error as VerifyRoomUnavailableError).status = status;
  }
  return error;
};

export const verifyRoomRequest = async ({
  roomName,
}: VerifyRoomParams): Promise<VerifyRoomResponse> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    SHORT_API_REQUEST_TIMEOUT_MS,
  );

  try {
    const response = await fetch('/api/daily/verify-room', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roomName }),
      signal: controller.signal,
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      if (data.reason === 'ended') {
        throw createError('Session has ended', 'ended');
      }
      if (data.reason === 'unavailable') {
        throw createError(
          'Appointment is unavailable',
          'unavailable',
          data.status,
        );
      }
      if (data.reason === 'not_found') {
        throw createError('Appointment not found', 'not_found');
      }
      throw createError(data.error || `HTTP ${response.status}`, 'error');
    }

    return data;
  } catch (error) {
    // Re-throw our custom errors as-is
    if ((error as VerifyRoomMutationError).reason) {
      throw error;
    }
    // Handle fetch-specific errors
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw createError('Request timed out. Please try again.', 'error');
      }
      if (error.message === 'Failed to fetch') {
        throw createError('Please check your internet connection.', 'error');
      }
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

type ParticipantType = 'provider' | 'patient';

/**
 * useQuery hook for proactive room verification in layouts.
 * Runs before DailyCallProvider renders to catch definitive errors early.
 * Automatically handles redirect when room is recreated with a new name.
 * See docs/verify-room.md for full architecture and flow diagram.
 */
export const useVerifyRoom = (
  roomName: string | undefined,
  participantType: ParticipantType,
) => {
  const router = useRouter();

  const query = useQuery<VerifyRoomResponse, VerifyRoomMutationError>({
    queryKey: ['verifyRoom', roomName],
    queryFn: () => verifyRoomRequest({ roomName: roomName as string }),
    enabled: !!roomName,
    retry: (failureCount, error) => {
      if (
        error.reason === 'ended' ||
        error.reason === 'unavailable' ||
        error.reason === 'not_found'
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 500,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  const isRedirecting = query.data != null && isRedirectResponse(query.data);

  useEffect(() => {
    if (query.data && isRedirectResponse(query.data)) {
      datadogLogs.logger.info(
        'Room recreated, redirecting to new room',
        callLogContext('room-verification', {
          roomId: roomName ?? '',
          participantType,
          newRoomName: query.data.newRoomName,
        }),
      );
      router.replace(`/${query.data.newRoomName}`);
    }
  }, [query.data, router, roomName, participantType]);

  return { ...query, isRedirecting };
};
